import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  HostListener,
  TemplateRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError } from 'rxjs/operators';

export interface ApiDropdownConfig {
  displayField: string;
  valueField?: string;
  multiple?: boolean;
  placeholder?: string;
  minLength?: number;
  debounceTime?: number;
  disabled?: boolean;
  showClear?: boolean;
  showDropdown?: boolean;
}

/**
 * Custom API Dropdown Component
 * A fully custom, in-house dropdown with API search capabilities
 * Built from scratch without any third-party UI library dependencies
 */
@Component({
  selector: 'app-api-dropdown',
  templateUrl: './api-dropdown.component.html',
  styleUrls: ['./api-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ApiDropdownComponent),
      multi: true
    }
  ]
})
export class ApiDropdownComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() config!: ApiDropdownConfig;
  @Input() searchFunction!: (query: string) => Observable<any[]>;
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() errorMessage: string = '';
  @Input() initialItems: any[] = [];
  @Input() itemTemplate?: TemplateRef<any>;
  @Input() selectedItemTemplate?: TemplateRef<any>;
  @Input() refreshTrigger?: number; // Trigger to force refresh

  @Output() onSelect = new EventEmitter<any>();
  @Output() onUnselect = new EventEmitter<any>();
  @Output() onClear = new EventEmitter<void>();
  @Output() onSearch = new EventEmitter<string>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownPanel') dropdownPanel!: ElementRef<HTMLDivElement>;

  // Component state
  isOpen = false;
  searchQuery = '';
  filteredItems: any[] = [];
  selectedValue: any = null;
  selectedItems: any[] = [];
  highlightedIndex = -1;
  isLoading = false;
  hasError = false;
  disabled = false;
  private needsReload = false;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    // Setup debounced search
    this.searchSubject
      .pipe(
        debounceTime(this.config.debounceTime || 300),
        distinctUntilChanged(),
        switchMap(query => {
          // Check minLength only if there's a query (not on initial load)
          if (query && query.length < (this.config.minLength || 0)) {
            return of([]);
          }
          this.isLoading = true;
          this.hasError = false;
          this.onSearch.emit(query);
          return this.searchFunction(query).pipe(
            catchError(error => {
              console.error('Search error:', error);
              this.hasError = true;
              this.isLoading = false;
              return of([]);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        this.filteredItems = items || [];
        this.isLoading = false;
      });

    // Load initial items
    if (this.initialItems && this.initialItems.length > 0) {
      this.filteredItems = this.initialItems;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for refreshTrigger changes to reload data
    if (changes['refreshTrigger'] && !changes['refreshTrigger'].firstChange) {
      console.log('[ApiDropdown] refreshTrigger changed:', changes['refreshTrigger'].currentValue, 'Label:', this.label);
      // Clear current items and mark for reload
      this.filteredItems = [];
      this.needsReload = true;
      if (this.isOpen) {
        // If dropdown is open, reload immediately
        console.log('[ApiDropdown] Dropdown is open, reloading immediately');
        this.performSearch(this.searchQuery);
        this.needsReload = false;
      } else {
        console.log('[ApiDropdown] Dropdown is closed, will reload on open');
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (this.config.multiple) {
      this.selectedItems = Array.isArray(value) ? value : [];
    } else {
      this.selectedValue = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Dropdown control
  toggleDropdown(): void {
    if (this.disabled) return;

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    console.log('[ApiDropdown] Opening dropdown:', this.label, 'needsReload:', this.needsReload, 'filteredItems.length:', this.filteredItems.length);

    this.isOpen = true;
    this.highlightedIndex = -1;
    this.onFocus.emit();

    // Load items if not already loaded or if reload is needed
    if ((this.filteredItems.length === 0 || this.needsReload) && !this.isLoading) {
      console.log('[ApiDropdown] Loading items...');
      const forceReload = this.needsReload;
      this.needsReload = false;
      if (forceReload) {
        // Bypass the subject to avoid distinctUntilChanged blocking
        this.forceSearch('');
      } else {
        this.performSearch('');
      }
    }

    // Focus search input inside panel
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 100);
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchQuery = '';
    this.highlightedIndex = -1;
    this.onTouched();
    this.onBlur.emit();
  }

  // Search handling
  onSearchInput(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery = query;
    this.performSearch(query);
  }

  performSearch(query: string): void {
    console.log('[ApiDropdown] performSearch called:', this.label, 'query:', query);
    this.searchSubject.next(query);
  }

  // Force search bypassing distinctUntilChanged for refresh scenarios
  private forceSearch(query: string): void {
    console.log('[ApiDropdown] forceSearch called:', this.label, 'query:', query);
    this.isLoading = true;
    this.hasError = false;
    this.onSearch.emit(query);
    this.searchFunction(query).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Search error:', error);
        this.hasError = true;
        this.isLoading = false;
        return of([]);
      })
    ).subscribe(items => {
      this.filteredItems = items || [];
      this.isLoading = false;
    });
  }

  // Item selection
  selectItem(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.config.multiple) {
      const valueField = this.config.valueField || 'id';
      const itemValue = this.getNestedProperty(item, valueField);
      const exists = this.selectedItems.some(
        selected => this.getNestedProperty(selected, valueField) === itemValue
      );

      if (exists) {
        // Item is already selected, remove it (uncheck)
        this.selectedItems = this.selectedItems.filter(
          selected => this.getNestedProperty(selected, valueField) !== itemValue
        );
        this.onChange(this.selectedItems);
        this.onUnselect.emit(item);
      } else {
        // Item is not selected, add it (check)
        this.selectedItems = [...this.selectedItems, item];
        this.onChange(this.selectedItems);
        this.onSelect.emit(item);
      }
      // Clear search query but don't trigger API call - items are already loaded
      this.searchQuery = '';
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    } else {
      this.selectedValue = item;
      this.onChange(item);
      this.onSelect.emit(item);
      this.closeDropdown();
    }
  }

  removeItem(item: any, event: Event): void {
    event.stopPropagation();

    const valueField = this.config.valueField || 'id';
    this.selectedItems = this.selectedItems.filter(
      selected => this.getNestedProperty(selected, valueField) !== this.getNestedProperty(item, valueField)
    );
    this.onChange(this.selectedItems);
    this.onUnselect.emit(item);
  }

  clearSelection(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.config.multiple) {
      this.selectedItems = [];
    } else {
      this.selectedValue = null;
    }

    this.onChange(this.config.multiple ? [] : null);
    this.onClear.emit();
    this.searchQuery = '';
    this.performSearch('');
  }

  // Select All functionality
  toggleSelectAll(event: Event): void {
    event.stopPropagation();

    if (this.allItemsSelected()) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  selectAll(): void {
    if (!this.config.multiple) return;

    const valueField = this.config.valueField || 'id';
    const uniqueItems = [...this.selectedItems];

    this.filteredItems.forEach(item => {
      const itemValue = this.getNestedProperty(item, valueField);
      const exists = uniqueItems.some(
        selected => this.getNestedProperty(selected, valueField) === itemValue
      );

      if (!exists) {
        uniqueItems.push(item);
      }
    });

    this.selectedItems = uniqueItems;
    this.onChange(this.selectedItems);
  }

  deselectAll(): void {
    if (!this.config.multiple) return;

    const valueField = this.config.valueField || 'id';
    const filteredItemValues = this.filteredItems.map(item =>
      this.getNestedProperty(item, valueField)
    );

    this.selectedItems = this.selectedItems.filter(
      selected => !filteredItemValues.includes(this.getNestedProperty(selected, valueField))
    );

    this.onChange(this.selectedItems);
  }

  allItemsSelected(): boolean {
    if (!this.config.multiple || this.filteredItems.length === 0) return false;

    const valueField = this.config.valueField || 'id';
    return this.filteredItems.every(item =>
      this.selectedItems.some(
        selected => this.getNestedProperty(selected, valueField) === this.getNestedProperty(item, valueField)
      )
    );
  }

  // Keyboard navigation
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredItems.length) {
          this.selectItem(this.filteredItems[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  highlightNext(): void {
    if (this.highlightedIndex < this.filteredItems.length - 1) {
      this.highlightedIndex++;
      this.scrollToHighlighted();
    }
  }

  highlightPrevious(): void {
    if (this.highlightedIndex > 0) {
      this.highlightedIndex--;
      this.scrollToHighlighted();
    }
  }

  scrollToHighlighted(): void {
    setTimeout(() => {
      const panel = this.dropdownPanel?.nativeElement;
      const highlighted = panel?.querySelector('.dropdown-item.highlighted');
      if (highlighted && panel) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  // Click outside detection
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  // Display helpers
  getDisplayValue(item: any): string {
    if (!item) return '';
    return this.getNestedProperty(item, this.config.displayField);
  }

  getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((prev, curr) => prev?.[curr], obj);
  }

  getPlaceholderText(): string {
    if (this.config.multiple && this.selectedItems.length > 0) {
      return '';
    }
    if (!this.config.multiple && this.selectedValue) {
      return this.getDisplayValue(this.selectedValue);
    }
    return this.config.placeholder || 'Search...';
  }

  isItemSelected(item: any): boolean {
    if (this.config.multiple) {
      const valueField = this.config.valueField || 'id';
      return this.selectedItems.some(
        selected => this.getNestedProperty(selected, valueField) === this.getNestedProperty(item, valueField)
      );
    }
    return false;
  }

  get hasValue(): boolean {
    return this.config.multiple ? this.selectedItems.length > 0 : !!this.selectedValue;
  }

  get showClearButton(): boolean {
    return this.config.showClear !== false && this.hasValue && !this.disabled;
  }
}
